import { useCallback, useEffect, useRef, useState } from 'react';

import { commitOwnerKey, discardGuestKey, generateGuestKey, type GuestKey, isConnectCode, unwrapBridgeKey } from '@/crypto';
import { transport } from '@/api/transport';
import { ApiError } from '@/api/types';
import { t } from '@/i18n';
import { noticeOf, type Notice } from '@/lib/errors';
import { bridgePath } from '@/lib/links';
import { sasEmoji, thisDevice, type Sas } from '@/lib/nearby';
import { setClaimToken } from '@/lib/storage';

export type JoinStage = 'input' | 'waiting' | 'done';

const POLL_MS = 1500;
const POLL_LIMIT_MS = 5 * 60 * 1000;

/** The other device did not hand the key over in time. */
class PollTimeout extends Error {}

/** The key the owner showed is not the one its code committed to: someone in between swapped it. */
class KeyMismatch extends Error {}

const wrongCode = (): string => t('useJoinWithCode.wrongCode');

function errorNotice(cause: unknown): Notice {
  if (cause instanceof ApiError && cause.code === 'code_invalid') return { title: wrongCode() };
  if (cause instanceof ApiError && cause.code === 'view_limit') return { title: t('share.noViewsLeft') };
  if (cause instanceof ApiError && cause.code === 'rate_limited') return { title: t('useJoinWithCode.rateLimited') };
  if (cause instanceof ApiError && cause.code === 'network') return noticeOf(cause, '', t('common.connect'));
  if (cause instanceof PollTimeout) return { title: t('useJoinWithCode.timeout') };
  if (cause instanceof KeyMismatch) return { title: t('useJoinWithCode.mismatch') };
  return { title: t('useJoinWithCode.failed') };
}

/**
 * The second device's side of pairing by code: sends its public key with the code, waits for the owner to show its own
 * (which must be the one its code committed to), shows the four emoji both screens must agree on, and opens the bridge
 * once the person pressed "They match" and the owner has wrapped the key for this device.
 */
export function useJoinWithCode() {
  const [stage, setStage] = useState<JoinStage>('input');
  const [error, setError] = useState<Notice | null>(null);
  const [emoji, setEmoji] = useState<readonly Sas[] | null>(null);
  const cancelled = useRef(false);
  // the person pressed "They match": until then nothing is unwrapped, whatever the server has already answered
  const confirmed = useRef(false);
  // read through a function: it changes while the awaits below run, which the compiler cannot see
  const isCancelled = useCallback(() => cancelled.current, []);
  const isConfirmed = useCallback(() => confirmed.current, []);

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  /** Resolves to the path of the bridge that opened, or null when the attempt failed or was called off. */
  const join = useCallback(async (code: string): Promise<string | null> => {
    cancelled.current = false;
    confirmed.current = false;
    setEmoji(null);
    if (!isConnectCode(code)) {
      setError({ title: wrongCode() });
      return null;
    }
    setError(null);
    setStage('waiting');
    let pair: GuestKey | null = null;
    try {
      pair = await generateGuestKey();
      // the code first: the owner's commitment comes back before this device's key goes anywhere
      const joined = await transport.joinWithCode({ code });
      await transport.sendGuestKey({ sessionId: joined.sessionId, pollToken: joined.pollToken, publicKey: pair.publicKey, device: thisDevice() });
      const started = Date.now();
      let shownKey: string | null = null;
      for (;;) {
        if (isCancelled()) {
          discardGuestKey(pair);
          return null;
        }
        const result = await transport.pollConnect({ sessionId: joined.sessionId, pollToken: joined.pollToken });
        if (isCancelled()) {
          discardGuestKey(pair);
          return null;
        }
        if (result.status !== 'waiting') {
          if (shownKey === null) {
            if ((await commitOwnerKey('connect', result.ownerPublicKey)) !== joined.ownerKeyHash) throw new KeyMismatch();
            shownKey = result.ownerPublicKey;
            setEmoji(await sasEmoji('connect', shownKey, pair.publicKey));
          } else if (result.ownerPublicKey !== shownKey) {
            throw new KeyMismatch();
          }
        }
        if (result.status === 'ready') {
          // the owner may be quicker than this person: keep the answer, unwrap only after the guest has compared too
          while (!isConfirmed()) {
            if (isCancelled()) {
              discardGuestKey(pair);
              return null;
            }
            if (Date.now() - started > POLL_LIMIT_MS) throw new PollTimeout();
            await new Promise((resolve) => window.setTimeout(resolve, 100));
          }
          const bridgeKey = await unwrapBridgeKey(pair, result.ownerPublicKey, result.wrappedKey, { flow: 'connect', id: joined.sessionId });
          setClaimToken(result.bridgeId, result.claimToken);
          setStage('done');
          return bridgePath(result.bridgeId, bridgeKey);
        }
        if (Date.now() - started > POLL_LIMIT_MS) throw new PollTimeout();
        await new Promise((resolve) => window.setTimeout(resolve, POLL_MS));
      }
    } catch (cause) {
      if (pair !== null) discardGuestKey(pair);
      if (!isCancelled()) {
        setStage('input');
        setEmoji(null);
        setError(errorNotice(cause));
      }
      return null;
    }
  }, [isCancelled, isConfirmed]);

  /** The person saw the same four pictures on the other screen: the key may be opened once it arrives. */
  const confirm = useCallback(() => {
    confirmed.current = true;
  }, []);

  /** The person saw other pictures than the owner's screen shows: stop, and the key is discarded. */
  const cancel = useCallback(() => {
    cancelled.current = true;
    confirmed.current = false;
    setStage('input');
    setEmoji(null);
  }, []);

  return { stage, error, emoji, join, confirm, cancel };
}
